import { Feature, LineString, GeoJSON, Point, Position } from 'geojson';
import { FeatureWaypoint } from '@motes-sdk';

/**
 * Verify that a GeoJSON object is a `Feature<LineString>`
 * @param geojson the GeoJSON object
 * @returns a type predicate narrowing the input to either be or not be a `Feature<LineString>`
 */
export function isFeatureLineString(geojson: GeoJSON): geojson is Feature<LineString> {
    if(!(geojson?.type == 'Feature')) return false;
    return geojson?.geometry?.type == 'LineString';
}

/**
 * Verify that a GeoJSON object is a `Feature<Point>`
 * @param geojson the GeoJSON object
 * @returns a type predicate narrowing the input to either be or not be a `Feature<Point>`
 */
export function isFeaturePoint(geojson: GeoJSON): geojson is Feature<Point> {
    if(!(geojson?.type == 'Feature')) return false;
    return geojson?.geometry?.type == 'Point';
}

/**
 * Verify that a GeoJSON object is a `Waypoint`
 * @param geojson the GeoJSON object
 * @returns a type predicate narrowing the input to either be or not be a `Waypoint`
 */
export function isFeatureWaypoint(geojson: GeoJSON): geojson is FeatureWaypoint {
    // First, verify that the feature is a `Feature<Point>`
    if(!isFeaturePoint(geojson)) return false;
    // Next, verify that it contains `Waypoint` properties
    return (
        geojson.properties.meta == 'waypoint' &&
        typeof geojson.properties.storyKey == 'string' &&
        (!geojson.properties.precedingWaypointIndex || typeof geojson.properties.precedingWaypointIndex == 'number')
    );
}

/**
 * Verify that a GeoJSON object is a `Feature<LineString>` with assosiated route segment properties
 * @param geojson the GeoJSON object
 * @returns a type predicate narrowing the input to either be or not be a `Feature<LineString>` with assosiated route segment properties
 */
export function isFeatureRouteSegment(geojson: GeoJSON): geojson is Feature<LineString, { meta: 'route-segment', storyKey: string, waypoints: [number, number][]; }> {
    // First, verify that the feature is a `Feature<LineString>`
    if(!isFeatureLineString(geojson)) return false;
    // Next, verify that it contains route segment properties
    return (
        geojson.properties.meta == 'route-segment' &&
        typeof geojson.properties.storyKey == 'string' &&
        Array.isArray(geojson.properties.waypoints)
    );
}

/**
 * Convert a `Position` (type alias for `number[]`) to a `[number, number]` tuple
 * @param position The position to convert
 * @returns A `[number, number]` tuple generated from the values at the first two indices of the input parameter, `null` if either is undefined
 */
export function positionToTuple(position: Position): [number, number] {
    return position[0] && position[1] ? [position[0], position[1]] : null;
}
